import React, { useState } from "react"
import {
  Row,
  Col,
  Pagination,
  PaginationItem,
  PaginationLink,
  Button,
  ButtonGroup,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap"
import { useSelector } from "react-redux"
import axios from "axios"
import config from "config/config"

const Reviews = ({ reviews = [] }) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(5)
  const [filter, setFilter] = useState("all") // 🆕 filter state
  const mode = useSelector(state => state.Layout.layoutMode)
  const { isConsolidatedView } = useSelector(state => state.organizationReducer)
  const userData = useSelector(state => state.Login.userData)

  const [selectedReview, setSelectedReview] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [draftLoading, setDraftLoading] = useState(false)
  const [draftError, setDraftError] = useState(null)

  const openReviewModal = review => {
    setSelectedReview(review)
    setDraftError(null)
    setIsModalOpen(true)
  }

  // AI-drafted review reply + tone check. Facebook's legacy Ratings API has
  // no confirmed public write endpoint for replying, so this stays
  // "draft, copy, and post yourself" — same pattern as Yelp/Healthgrade/Vitals.
  const draftReply = async () => {
    if (!selectedReview) return
    setDraftLoading(true)
    setDraftError(null)
    try {
      const response = await axios.post(
        `${config.backendUrl}/facebook/reviews/${selectedReview.id}/draft-reply`,
        {
          organizationId: selectedReview.organizationId,
          pageId: selectedReview.pageId,
          reviewText: selectedReview.review_text,
          recommendationType: selectedReview.recommendation_type,
        },
        { headers: { Authorization: `Bearer ${userData.token}` } },
      )
      setSelectedReview(prev => ({
        ...prev,
        replyText: response.data.replyText,
        toneFlags: response.data.toneFlags || [],
      }))
    } catch (err) {
      setDraftError("Error generating reply draft. Please try again.")
    } finally {
      setDraftLoading(false)
    }
  }

  const copyAndOpen = async () => {
    if (!selectedReview) return
    if (selectedReview.replyText) {
      try {
        await navigator.clipboard.writeText(selectedReview.replyText)
      } catch {}
    }
    window.open(
      `https://www.facebook.com/${selectedReview.pageId}/reviews`,
      "_blank",
      "noopener,noreferrer",
    )
  }

  const markResponded = async () => {
    if (!selectedReview) return
    try {
      await axios.patch(
        `${config.backendUrl}/facebook/reviews/${selectedReview.id}/mark-responded`,
        {
          organizationId: selectedReview.organizationId,
          pageId: selectedReview.pageId,
        },
        { headers: { Authorization: `Bearer ${userData.token}` } },
      )
      setSelectedReview(prev => ({ ...prev, respondedManually: true }))
    } catch (err) {
      setDraftError("Error marking review as responded.")
    }
  }

  // ✅ Filter reviews based on recommendation type
  const filteredReviews =
    filter === "all"
      ? reviews
      : reviews.filter(r => r.recommendation_type === filter)

  // Pagination logic
  const totalPages = Math.ceil(filteredReviews.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentReviews = filteredReviews.slice(startIndex, endIndex)

  const paginate = pageNumber => setCurrentPage(pageNumber)

  const formatDate = dateString => {
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  const getRecommendationBadge = type => {
    const isPositive = type === "positive"
    return (
      <span
        style={{
          padding: "4px 12px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: "600",
          backgroundColor: isPositive ? "#dcfce7" : "#fee2e2",
          color: isPositive ? "#166534" : "#991b1b",
        }}
      >
        {isPositive ? "Positive" : "Negative"}
      </span>
    )
  }

  return (
    <div
      style={{
        padding: "0px 20px",
        borderRadius: "20px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        backgroundColor: mode === "dark" ? "#252f40" : "#fff",
        marginBottom: "20px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between", // Reviews left, Filters right
          alignItems: "center", // Vertically center both
          padding: "20px 20px 0 20px",
          paddingTop: "20px",
          flexWrap: "wrap", // Responsive for smaller screens
        }}
      >
        {/* Left side — Title */}
        <h4
          style={{
            fontWeight: "600",
            fontSize: "16px",
            color: mode === "dark" ? "#9ca3af" : "#374151",
            margin: 0,
          }}
        >
          Reviews
        </h4>

        {/* Right side — Filters */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >
          <ButtonGroup>
            <Button
              size="sm"
              onClick={() => {
                setFilter("all")
                setCurrentPage(1)
              }}
              style={{
                backgroundColor: filter === "all" ? "#2990DF" : "transparent",
                color:
                  filter === "all"
                    ? "#fff"
                    : mode === "dark"
                      ? "#d1d5db"
                      : "#374151",
                borderColor:
                  filter === "all"
                    ? "#2990DF"
                    : mode === "dark"
                      ? "#444"
                      : "#ccc",
                boxShadow: "none",
              }}
            >
              All
            </Button>

            <Button
              size="sm"
              onClick={() => {
                setFilter("positive")
                setCurrentPage(1)
              }}
              style={{
                backgroundColor:
                  filter === "positive" ? "#2990DF" : "transparent",
                color:
                  filter === "positive"
                    ? "#fff"
                    : mode === "dark"
                      ? "#d1d5db"
                      : "#374151",
                borderColor:
                  filter === "positive"
                    ? "#2990DF"
                    : mode === "dark"
                      ? "#444"
                      : "#ccc",
                boxShadow: "none",
              }}
            >
              Positive
            </Button>

            <Button
              size="sm"
              onClick={() => {
                setFilter("negative")
                setCurrentPage(1)
              }}
              style={{
                backgroundColor:
                  filter === "negative" ? "#2990DF" : "transparent",
                color:
                  filter === "negative"
                    ? "#fff"
                    : mode === "dark"
                      ? "#d1d5db"
                      : "#374151",
                borderColor:
                  filter === "negative"
                    ? "#2990DF"
                    : mode === "dark"
                      ? "#444"
                      : "#ccc",
                boxShadow: "none",
              }}
            >
              Negative
            </Button>
          </ButtonGroup>
        </div>
      </div>

      {/* Table */}
      <Row>
        <Col xl={12}>
          <div
            style={{
              backgroundColor: mode === "dark" ? "#252f40" : "#fff",
              overflow: "hidden",
              padding: "20px",
            }}
          >
            {/* Table Header */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isConsolidatedView
                  ? "60px 150px 150px 120px 1fr 100px"
                  : "60px 150px 120px 1fr 100px",
                gap: "16px",
                padding: "16px 20px",
                backgroundColor: mode === "dark" ? "#1a2332" : "#f9fafb",
                borderBottom: `1px solid ${
                  mode === "dark" ? "#3a4556" : "#e5e7eb"
                }`,
                fontWeight: "600",
                fontSize: "14px",
                color: mode === "dark" ? "#9ca3af" : "#374151",
              }}
            >
              <div>#</div>
              <div>Date</div>
              {isConsolidatedView && <div>Organization</div>}
              <div>Recommendation</div>
              <div
                style={{
                  paddingLeft: "30px",
                }}
              >
                Review
              </div>
              {!isConsolidatedView && (
                <div style={{ textAlign: "center" }}>Action</div>
              )}
            </div>

            {/* Table Body */}
            {currentReviews.length > 0 ? (
              currentReviews.map((review, index) => (
                <div
                  key={index}
                  style={{
                    display: "grid",
                    gridTemplateColumns: isConsolidatedView
                      ? "60px 150px 150px 120px 1fr 100px"
                      : "60px 150px 120px 1fr 100px",
                    gap: "16px",
                    padding: "16px 20px",
                    borderBottom:
                      index < currentReviews.length - 1
                        ? `1px solid ${mode === "dark" ? "#3a4556" : "#e5e7eb"}`
                        : "none",
                    backgroundColor: mode === "dark" ? "#252f40" : "#fff",
                    color: mode === "dark" ? "#d1d5db" : "#374151",
                    fontSize: "14px",
                    transition: "background-color 0.2s",
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor =
                      mode === "dark" ? "#2d3748" : "#f9fafb"
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor =
                      mode === "dark" ? "#252f40" : "#fff"
                  }}
                >
                  {/* # Numbering */}
                  <div
                    style={{
                      color: mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    {startIndex + index + 1}
                  </div>

                  {/* Date */}
                  <div
                    style={{
                      color: mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    {formatDate(review.created_time)}
                  </div>

                  {/* Organization Name */}
                  {isConsolidatedView && <div>{review.organizationName}</div>}
                  {/* Recommendation */}
                  <div>
                    {getRecommendationBadge(review.recommendation_type)}
                  </div>

                  {/* Review Text */}
                  <div style={{ lineHeight: "1.5", paddingLeft: "30px" }}>
                    {review.review_text}
                  </div>
                  {/* Action Button */}
                  {!isConsolidatedView && (
                    <div style={{ textAlign: "center" }}>
                      <Button
                        size="sm"
                        style={{
                          backgroundColor: "#2990DF",
                          border: "none",
                          padding: "4px 10px",
                          fontSize: "12px",
                          borderRadius: "6px",
                        }}
                        onClick={() => openReviewModal(review)}
                      >
                        Respond
                      </Button>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div
                style={{
                  padding: "40px 20px",
                  textAlign: "center",
                  color: mode === "dark" ? "#9ca3af" : "#6b7280",
                }}
              >
                No reviews available
              </div>
            )}
          </div>
        </Col>
      </Row>

      {/* Pagination */}
      <div
        style={{
          display: "flex",
          justifyContent: "left",
          padding: "16px",
        }}
      >
        <Pagination className="mb-0 justify-content-center mt-auto">
          <PaginationItem disabled={currentPage === 1}>
            <PaginationLink
              previous
              onClick={() => paginate(currentPage - 1)}
              style={{
                color: mode === "dark" ? "#fff" : "#000",
                backgroundColor: "transparent",
                borderColor: mode === "dark" ? "#444" : "#dee2e6",
              }}
            />
          </PaginationItem>

          {Array.from({ length: totalPages }).map((_, i) => (
            <PaginationItem active={i + 1 === currentPage} key={i}>
              <PaginationLink
                onClick={() => paginate(i + 1)}
                style={{
                  borderColor:
                    i + 1 === currentPage
                      ? "#2990DF"
                      : mode === "dark"
                        ? "#444"
                        : "#dee2e6",
                  backgroundColor:
                    i + 1 === currentPage ? "#2990DF" : "transparent",
                  color:
                    i + 1 === currentPage
                      ? "#fff"
                      : mode === "dark"
                        ? "#fff"
                        : "#000",
                  boxShadow: "none",
                }}
              >
                {i + 1}
              </PaginationLink>
            </PaginationItem>
          ))}

          <PaginationItem disabled={currentPage === totalPages}>
            <PaginationLink
              next
              onClick={() => paginate(currentPage + 1)}
              style={{
                color: mode === "dark" ? "#fff" : "#000",
                backgroundColor: "transparent",
                borderColor: mode === "dark" ? "#444" : "#dee2e6",
              }}
            />
          </PaginationItem>
        </Pagination>
      </div>

      <Modal isOpen={isModalOpen} toggle={() => setIsModalOpen(!isModalOpen)}>
        <ModalHeader toggle={() => setIsModalOpen(!isModalOpen)}>
          Review Details
        </ModalHeader>
        <ModalBody>
          {selectedReview && (
            <>
              <p>
                <strong>Date:</strong> {formatDate(selectedReview.created_time)}
              </p>
              <p>
                <strong>Recommendation:</strong>{" "}
                {getRecommendationBadge(selectedReview.recommendation_type)}
              </p>
              <hr />
              <p>
                <strong>Review Text:</strong>
              </p>
              <p>{selectedReview.review_text}</p>

              <hr />
              <p className="mb-2">
                <strong>AI Reply Draft</strong>{" "}
                {selectedReview.respondedManually && (
                  <span className="badge bg-success ms-2">Responded</span>
                )}
              </p>

              {draftError && <p className="text-danger small">{draftError}</p>}

              {!selectedReview.replyText ? (
                <Button
                  size="sm"
                  onClick={draftReply}
                  disabled={draftLoading}
                  style={{ backgroundColor: "#2990DF", border: "none" }}
                >
                  {draftLoading ? "Drafting..." : "Draft Reply"}
                </Button>
              ) : (
                <>
                  <p style={{ whiteSpace: "pre-wrap" }}>
                    {selectedReview.replyText}
                  </p>
                  {(selectedReview.toneFlags || []).length > 0 && (
                    <div className="mb-2">
                      {selectedReview.toneFlags.map((flag, i) => (
                        <p key={i} className="text-warning small mb-1">
                          ⚠ {flag}
                        </p>
                      ))}
                    </div>
                  )}
                  <div className="d-flex gap-2 flex-wrap">
                    <Button
                      color="secondary"
                      size="sm"
                      onClick={draftReply}
                      disabled={draftLoading}
                    >
                      {draftLoading ? "Regenerating..." : "Regenerate"}
                    </Button>
                    <Button
                      size="sm"
                      onClick={copyAndOpen}
                      style={{ backgroundColor: "#2990DF", border: "none" }}
                    >
                      Copy & Open Facebook
                    </Button>
                    {!selectedReview.respondedManually && (
                      <Button color="success" size="sm" onClick={markResponded}>
                        Mark as Responded
                      </Button>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </ModalBody>
      </Modal>
    </div>
  )
}

export default Reviews
